// Tiny fetch wrapper.
//
// All requests are sent to the same origin as the page, even when the page
// is loaded through an ngrok tunnel. We always pass an absolute URL to fetch
// (constructed from `window.location.origin`) so that:
//   1. Same-origin cookies are sent automatically.
//   2. The browser doesn't carry forward credentials embedded in the URL
//      (e.g. https://user:pass@host) which would crash the fetch constructor.
//
// Behind an ngrok tunnel with basic auth, Chrome prompts the operator the
// first time and caches the credentials for the session. Subsequent fetches
// from the same origin include `Authorization: Basic ...` automatically.

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type ApiOptions = Omit<RequestInit, "body"> & { body?: unknown };

export async function api<T = unknown>(
  path: string,
  options: ApiOptions = {}
): Promise<T> {
  const method = options.method ?? (options.body ? "POST" : "GET");

  // Always use the current page's origin as the base URL, so we never embed
  // any credentials from the document URL into a fetch.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const fullUrl = API_BASE
    ? (API_BASE.startsWith("http://") || API_BASE.startsWith("https://")
        ? `${API_BASE}${path}`
        : `${origin}${API_BASE}${path}`)
    : `${origin}${path}`;

  const res = await fetch(fullUrl, {
    method,
    headers: {
      ...(options.body === undefined ? {} : { "Content-Type": "application/json" }),
      ...((options.headers as Record<string, string> | undefined) ?? {}),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  const isJson = res.headers.get("content-type")?.includes("application/json");
  const data = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, data, message);
  }
  return data as T;
}

export const apiUrl = API_BASE;
